import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { geocodeAddress } from "@/lib/geocode";
import ResidenceVerificationCard from "@/components/ResidenceVerificationCard";
import {
  Car, Users, ArrowRight, Clock, MapPin, Plus, CheckCircle2,
  X, Lock, Send, UserCheck, UserX, Hourglass, ShieldCheck, Calendar, Star, MapPinned,
} from "lucide-react";

const DAYS = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"];

function formatTripDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "short" });
}

/**
 * Carpool — drivers post trips, members apply, driver moderates.
 */
export default function CarpoolSection({ eventFilter = null }) {
  const { user } = useAuth();
  const isAuthed = !!user && user !== false;
  const isVerified = isAuthed && user.residence_status === "verified";
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [applyOpen, setApplyOpen] = useState(null); // selected trip
  const [moderateOpen, setModerateOpen] = useState(null); // driver's view of trip applications
  const [eventTitle, setEventTitle] = useState(null);

  const load = () => {
    setLoading(true);
    const url = eventFilter ? `/carpool/?event_id=${eventFilter}` : "/carpool/";
    api.get(url).then((r) => setItems(r.data || [])).catch(() => setItems([])).finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [eventFilter]);

  useEffect(() => {
    if (eventFilter) {
      api.get("/public/events").then((r) => {
        const ev = (r.data?.items || []).find((e) => e.id === eventFilter);
        setEventTitle(ev?.title || null);
      }).catch(() => {});
    } else {
      setEventTitle(null);
    }
  }, [eventFilter]);

  const handleAddClick = () => {
    if (!isVerified) {
      toast.error("Justificatif de domicile requis pour proposer un trajet", {
        description: "Ajoutez votre justificatif dans la section ci-dessous.",
      });
      // Scroll to residence card
      setTimeout(() => {
        document.querySelector('[data-testid="residence-card"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 100);
      return;
    }
    setOpen(true);
  };

  return (
    <section id="covoiturage" className="bg-mint-50/60 border-y border-mint-200/50 scroll-mt-32" data-testid="carpool-section">
      <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-8 py-12 sm:py-16">
        {eventFilter && eventTitle && (
          <div className="mb-5 phenix-card bg-sun-50 border-sun-200 p-3 flex flex-wrap items-center justify-between gap-2" data-testid="event-filter-banner">
            <div className="text-[13px] text-plum-900">
              <Calendar size={13} className="inline mr-1.5 text-sun-700" />
              Trajets liés à <strong>« {eventTitle} »</strong>
            </div>
            <Link to="/covoiturage" className="text-[12px] text-plum-700 hover:text-plum-900 font-bold underline">
              Voir tous les trajets
            </Link>
          </div>
        )}
        <div className="grid lg:grid-cols-[1fr_auto] items-end gap-4 mb-7">
          <div>
            <div className="phenix-eyebrow mb-3 inline-flex items-center gap-1.5">
              <Car size={11} strokeWidth={2.5} /> Co-voiturage Istréen
            </div>
            <h2 className="heading-display text-[1.8rem] sm:text-[2.4rem] text-plum-900 leading-[1.1]">
              Partagez vos trajets,
              <br className="hidden sm:block" />
              <span className="text-mint-600">à plusieurs c&apos;est mieux.</span>
            </h2>
            <p className="mt-3 text-[15px] text-plum-700/75 max-w-2xl">
              Réservé aux habitants et commerçants Istréens vérifiés. Trajets ponctuels (date + heure), liés ou non à un événement de l&apos;agenda. Le chauffeur valide les candidatures après consultation de votre profil.
            </p>
          </div>
          {isAuthed ? (
            <div className="flex items-center gap-2">
              {isVerified && (
                <span
                  className="hidden md:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-mint-100 text-mint-700 text-[12px] font-bold"
                  data-testid="residence-verified-pill"
                >
                  <ShieldCheck size={13} /> Résidence vérifiée
                </span>
              )}
              <button
                onClick={handleAddClick}
                data-testid="carpool-add-btn"
                className="rounded-full bg-mint-600 hover:bg-mint-700 text-white h-12 px-5 inline-flex items-center gap-2 font-bold shadow-lg shadow-mint-600/25 transition-transform hover:-translate-y-0.5 disabled:opacity-50"
              >
                {isVerified ? (<><Plus size={15} /> Proposer un trajet</>) : (<><ShieldCheck size={15} /> Vérification requise</>)}
              </button>
            </div>
          ) : (
            <Link
              to="/login"
              className="rounded-full bg-mint-600 hover:bg-mint-700 text-white h-12 px-5 inline-flex items-center gap-2 font-bold shadow-lg shadow-mint-600/25"
            >
              <Lock size={14} /> Se connecter pour proposer
            </Link>
          )}
        </div>

        {/* Residence verification banner */}
        {isAuthed && !isVerified && (
          <div className="mb-6">
            <ResidenceVerificationCard />
          </div>
        )}

        {/* List */}
        {loading ? (
          <div className="phenix-card p-10 animate-pulse bg-white" />
        ) : items.length === 0 ? (
          <div className="phenix-card bg-white p-8 sm:p-12 text-center" data-testid="carpool-empty">
            <Car size={28} className="mx-auto text-plum-700/30 mb-3" />
            <h4 className="font-display font-bold text-plum-900 text-lg mb-1">
              Aucun trajet proposé pour le moment
            </h4>
            <p className="text-[13px] text-plum-700/65 max-w-sm mx-auto">
              Soyez le premier chauffeur à proposer un trajet — la communauté grandit chaque jour.
            </p>
            {isAuthed && isVerified ? (
              <button onClick={() => setOpen(true)} className="mt-4 rounded-full bg-plum-700 hover:bg-plum-800 text-white px-5 py-2.5 text-[13px] font-bold inline-flex items-center gap-1.5">
                Ajouter mon trajet <ArrowRight size={13} />
              </button>
            ) : !isAuthed ? (
              <Link to="/login" className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-plum-700 hover:bg-plum-800 text-white px-5 py-2.5 text-[13px] font-bold">
                Se connecter <ArrowRight size={13} />
              </Link>
            ) : null}
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="carpool-list">
            {items.map((it) => {
              const isDriver = isAuthed && it.driver_id === String(user.id);
              const myApp = isAuthed && it.applications?.find((a) => a.user_id === String(user.id));
              const remaining = Math.max(0, (it.seats || 0) - (it.seats_taken || 0));
              const whenLabel = it.trip_date
                ? `${formatTripDate(it.trip_date)} · ${it.trip_time || it.time || "—"}`
                : `${it.day || "—"} · ${it.time || it.trip_time || "—"}${it.recurring ? " · hebdo" : ""}`;
              const hasSolidary = (it.applications || []).some((a) => a.is_solidary);
              return (
                <article key={it.id} className="phenix-card phenix-card-hover bg-white p-4 flex flex-col" data-testid={`carpool-item-${it.id}`}>
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="phenix-pill bg-mint-100 text-mint-700 inline-flex items-center gap-1">
                        <Car size={10} strokeWidth={2.4} /> {it.driver_role === "commercant" ? "Commerçant" : "Trajet"}
                      </span>
                      {hasSolidary && (
                        <span className="phenix-pill bg-sun-100 text-sun-800 text-[10px] inline-flex items-center gap-1" data-testid={`solidary-badge-${it.id}`}>
                          🎁 Solidaire
                        </span>
                      )}
                    </div>
                    <span className={`phenix-pill text-[10px] ${remaining > 0 ? "bg-sun-100 text-sun-700" : "bg-cream-100 text-plum-700/55"}`}>
                      {remaining > 0 ? `${remaining} place${remaining > 1 ? "s" : ""} libre${remaining > 1 ? "s" : ""}` : "Complet"}
                    </span>
                  </div>
                  <Link to={`/covoiturage/${it.id}`} className="block group" data-testid={`carpool-link-${it.id}`}>
                    <div className="space-y-1 text-[13px] text-plum-700/80 mb-3 flex-1">
                      <div className="flex items-start gap-1.5">
                        <MapPin size={13} className="mt-0.5 text-plum-500 shrink-0" />
                        <span className="leading-snug group-hover:text-plum-900 transition-colors">
                          <strong className="text-plum-900">{it.from_location}</strong>
                          <span className="text-plum-700/55"> → </span>
                          <strong className="text-plum-900">{it.to_location}</strong>
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        {it.trip_date ? <Calendar size={13} className="text-plum-500 shrink-0" /> : <Clock size={13} className="text-plum-500 shrink-0" />}
                        <span>{whenLabel}</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Users size={13} className="text-plum-500 shrink-0" />
                        <span>Chauffeur : <strong className="text-plum-900">{it.contact_name}</strong></span>
                      </div>
                      {it.note && <p className="text-[12px] text-plum-700/65 italic line-clamp-2 pt-1">« {it.note} »</p>}
                    </div>
                  </Link>

                  <div className="border-t border-cream-100 pt-2">
                    {isDriver ? (
                      <Link
                        to={`/covoiturage/${it.id}`}
                        className="w-full rounded-full bg-plum-700 hover:bg-plum-800 text-white px-4 py-2 text-[12px] font-bold inline-flex items-center justify-center gap-1.5"
                        data-testid={`carpool-manage-${it.id}`}
                      >
                        <UserCheck size={12} />
                        Gérer ({it.applications?.length || 0} candidat{(it.applications?.length || 0) > 1 ? "s" : ""})
                      </Link>
                    ) : myApp ? (
                      <Link to={`/covoiturage/${it.id}`}>
                        <ApplicationBadge status={myApp.status} />
                      </Link>
                    ) : !isAuthed ? (
                      <Link to="/login" className="w-full rounded-full bg-mint-600 hover:bg-mint-700 text-white px-4 py-2 text-[12px] font-bold inline-flex items-center justify-center gap-1.5">
                        <Lock size={12} /> Connectez-vous pour candidater
                      </Link>
                    ) : remaining > 0 ? (
                      <Link
                        to={`/covoiturage/${it.id}`}
                        className="w-full rounded-full bg-mint-600 hover:bg-mint-700 text-white px-4 py-2 text-[12px] font-bold inline-flex items-center justify-center gap-1.5"
                        data-testid={`carpool-apply-${it.id}`}
                      >
                        <Send size={12} /> Voir & candidater
                      </Link>
                    ) : (
                      <div className="w-full rounded-full bg-cream-100 text-plum-700/55 px-4 py-2 text-[12px] font-bold text-center">
                        Complet
                      </div>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>

      <NewTripDialog open={open} onClose={() => { setOpen(false); load(); }} />
      <ApplyDialog trip={applyOpen} onClose={() => { setApplyOpen(null); load(); }} />
      <ModerateDialog trip={moderateOpen} onClose={() => { setModerateOpen(null); load(); }} />
    </section>
  );
}

function ApplicationBadge({ status }) {
  if (status === "accepted") {
    return (
      <div className="w-full rounded-full bg-mint-100 text-mint-700 px-4 py-2 text-[12px] font-bold inline-flex items-center justify-center gap-1.5">
        <CheckCircle2 size={12} /> Candidature acceptée
      </div>
    );
  }
  if (status === "rejected") {
    return (
      <div className="w-full rounded-full bg-cream-100 text-plum-700/55 px-4 py-2 text-[12px] font-bold inline-flex items-center justify-center gap-1.5">
        <X size={12} /> Candidature refusée
      </div>
    );
  }
  return (
    <div className="w-full rounded-full bg-sun-100 text-sun-700 px-4 py-2 text-[12px] font-bold inline-flex items-center justify-center gap-1.5">
      <Hourglass size={12} /> En attente du chauffeur
    </div>
  );
}

function NewTripDialog({ open, onClose }) {
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [events, setEvents] = useState([]);
  const todayIso = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    from_location: "", to_location: "",
    trip_date: todayIso, trip_time: "08:00",
    seats: 3, recurring: false,
    event_id: "",
    contact_phone: "", note: "",
  });
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (!open) return;
    api.get("/public/events").then((r) => setEvents(r.data?.items || [])).catch(() => setEvents([]));
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.from_location || !form.to_location) return toast.error("Départ et arrivée requis");
    if (!form.trip_date) return toast.error("Date du trajet requise");
    setBusy(true);
    try {
      // Géocodage best-effort en parallèle
      const [g1, g2] = await Promise.all([
        geocodeAddress(form.from_location),
        geocodeAddress(form.to_location),
      ]);
      const payload = {
        ...form,
        kind: "offer",
        from_lat: g1?.lat, from_lng: g1?.lng,
        to_lat: g2?.lat, to_lng: g2?.lng,
        event_id: form.event_id || null,
        // Compat : day déduit côté serveur depuis trip_date
        time: form.trip_time,
      };
      await api.post("/carpool/", payload);
      setDone(true);
      window.dispatchEvent(new CustomEvent("istres-action-completed", { detail: { type: "carpool" } }));
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const close = () => {
    setDone(false);
    setForm({ from_location: "", to_location: "", trip_date: todayIso, trip_time: "08:00", seats: 3, recurring: false, event_id: "", contact_phone: "", note: "" });
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto bg-cream-50 border-cream-200 rounded-2xl" data-testid="carpool-dialog">
        <DialogHeader>
          <div className="phenix-eyebrow inline-flex items-center gap-1.5 mb-1">
            <Car size={11} strokeWidth={2.5} /> Proposer un trajet
          </div>
          <DialogTitle className="heading-display text-[1.4rem] text-plum-900">Nouveau trajet</DialogTitle>
        </DialogHeader>

        {done ? (
          <div className="text-center py-7">
            <div className="w-14 h-14 rounded-full bg-mint-100 text-mint-600 grid place-items-center mx-auto mb-3">
              <CheckCircle2 size={28} strokeWidth={2.2} />
            </div>
            <h3 className="font-display font-extrabold text-lg text-plum-900 mb-1">Trajet publié ✓</h3>
            <p className="text-[13px] text-plum-700/75 max-w-sm mx-auto">Les Istréens peuvent maintenant candidater. Vous serez notifié à chaque demande et pourrez consulter leur profil avant de valider.</p>
            <Button onClick={close} className="rounded-full bg-plum-700 hover:bg-plum-800 text-white h-11 px-5 mt-4">Fermer</Button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <div className="grid sm:grid-cols-2 gap-2.5">
              <Field label="Départ *" v={form.from_location} on={(v) => upd("from_location", v)} placeholder="Quartier Trigance, Istres" testid="carpool-from" />
              <Field label="Arrivée *" v={form.to_location} on={(v) => upd("to_location", v)} placeholder="Centre-ville, Istres" testid="carpool-to" />
            </div>
            <p className="text-[11px] text-plum-700/55 -mt-1 inline-flex items-center gap-1">
              <MapPinned size={11} /> Le géocodage automatique placera votre trajet sur la carte.
            </p>
            <div className="grid grid-cols-3 gap-2.5">
              <div className="space-y-1.5 col-span-2 sm:col-span-1">
                <Label className="text-[11px] uppercase tracking-wider text-plum-700/70 font-semibold">Date</Label>
                <Input type="date" min={todayIso} value={form.trip_date} onChange={(e) => upd("trip_date", e.target.value)} className="rounded-xl h-11 bg-white" data-testid="carpool-date" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[11px] uppercase tracking-wider text-plum-700/70 font-semibold">Heure</Label>
                <Input type="time" value={form.trip_time} onChange={(e) => upd("trip_time", e.target.value)} className="rounded-xl h-11 bg-white" data-testid="carpool-time" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[11px] uppercase tracking-wider text-plum-700/70 font-semibold">Places</Label>
                <Input type="number" min="1" max="8" value={form.seats} onChange={(e) => upd("seats", Number(e.target.value))} className="rounded-xl h-11 bg-white" data-testid="carpool-seats" />
              </div>
            </div>
            {/* Lier à un événement */}
            {events.length > 0 && (
              <div className="space-y-1.5">
                <Label className="text-[11px] uppercase tracking-wider text-plum-700/70 font-semibold inline-flex items-center gap-1">
                  <Calendar size={11} /> Lié à un événement de l&apos;agenda (optionnel)
                </Label>
                <Select value={form.event_id || "none"} onValueChange={(v) => upd("event_id", v === "none" ? "" : v)}>
                  <SelectTrigger data-testid="carpool-event" className="rounded-xl h-11 bg-white"><SelectValue placeholder="Aucun" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Aucun événement —</SelectItem>
                    {events.slice(0, 30).map((ev) => (
                      <SelectItem key={ev.id} value={ev.id}>{ev.title} ({ev.start_at?.slice(0, 10)})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-plum-700/55">Votre trajet apparaîtra dans l&apos;agenda à côté de cet événement.</p>
              </div>
            )}
            <label className="flex items-center gap-2 text-[13px] text-plum-700 cursor-pointer">
              <input type="checkbox" checked={form.recurring} onChange={(e) => upd("recurring", e.target.checked)} className="rounded" data-testid="carpool-recurring" />
              Trajet hebdomadaire (chaque semaine à cette date/heure)
            </label>
            <Field label="Téléphone (optionnel)" type="tel" v={form.contact_phone} on={(v) => upd("contact_phone", v)} placeholder="06 …" testid="carpool-phone" />
            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase tracking-wider text-plum-700/70 font-semibold">Note (optionnel)</Label>
              <Textarea value={form.note} onChange={(e) => upd("note", e.target.value)} placeholder="Non-fumeur, contre 2 € de participation…" rows={2} data-testid="carpool-note" className="rounded-xl bg-white" />
            </div>
            <Button type="submit" disabled={busy} data-testid="carpool-submit" className="rounded-full bg-plum-700 hover:bg-plum-800 text-white w-full h-12 mt-2 font-bold inline-flex items-center justify-center gap-2">
              {busy ? "Géocodage & publication…" : <>Publier le trajet <ArrowRight size={14} /></>}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ApplyDialog({ trip, onClose }) {
  const [message, setMessage] = useState("");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/carpool/${trip.id}/apply`, { message, phone });
      setDone(true);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const close = () => { setMessage(""); setPhone(""); setDone(false); onClose(); };

  return (
    <Dialog open={!!trip} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-md bg-cream-50 border-cream-200 rounded-2xl" data-testid="carpool-apply-dialog">
        {trip && (
          <>
            <DialogHeader>
              <div className="phenix-eyebrow inline-flex items-center gap-1.5 mb-1">
                <Send size={11} /> Candidater
              </div>
              <DialogTitle className="heading-display text-[1.3rem] text-plum-900">
                {trip.from_location} → {trip.to_location}
              </DialogTitle>
              <p className="text-[12px] text-plum-700/70">{trip.day} · {trip.time} · Chauffeur : {trip.contact_name}</p>
            </DialogHeader>

            {done ? (
              <div className="text-center py-5">
                <div className="w-14 h-14 rounded-full bg-mint-100 text-mint-600 grid place-items-center mx-auto mb-2">
                  <CheckCircle2 size={28} />
                </div>
                <h3 className="font-display font-extrabold text-lg text-plum-900">Candidature envoyée ✓</h3>
                <p className="text-[13px] text-plum-700/75 mt-1">Vous serez notifié dès que le chauffeur a répondu.</p>
                <Button onClick={close} className="rounded-full bg-plum-700 hover:bg-plum-800 text-white h-11 px-5 mt-4">Fermer</Button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-3">
                <Field label="Téléphone (optionnel)" type="tel" v={phone} on={setPhone} placeholder="06 …" testid="apply-phone" />
                <div className="space-y-1.5">
                  <Label className="text-[11px] uppercase tracking-wider text-plum-700/70 font-semibold">Message au chauffeur</Label>
                  <Textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={3} placeholder="Bonjour, je travaille en centre-ville et serais intéressé…" data-testid="apply-message" className="rounded-xl bg-white" />
                </div>
                <Button type="submit" disabled={busy} data-testid="apply-submit" className="rounded-full bg-mint-600 hover:bg-mint-700 text-white w-full h-12 mt-2 font-bold inline-flex items-center justify-center gap-2">
                  Envoyer ma candidature <ArrowRight size={14} />
                </Button>
              </form>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ModerateDialog({ trip, onClose }) {
  const [busy, setBusy] = useState(null);

  const moderate = async (appId, status) => {
    setBusy(appId);
    try {
      await api.patch(`/carpool/${trip.id}/applications/${appId}`, { status });
      toast.success(status === "accepted" ? "Candidature acceptée" : "Candidature refusée");
      onClose();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(null); }
  };

  if (!trip) return null;
  const apps = trip.applications || [];

  return (
    <Dialog open={!!trip} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg bg-cream-50 border-cream-200 rounded-2xl max-h-[90vh] overflow-y-auto" data-testid="carpool-moderate-dialog">
        <DialogHeader>
          <div className="phenix-eyebrow inline-flex items-center gap-1.5 mb-1">
            <UserCheck size={11} /> Gérer les candidatures
          </div>
          <DialogTitle className="heading-display text-[1.3rem] text-plum-900">
            {trip.from_location} → {trip.to_location}
          </DialogTitle>
          <p className="text-[12px] text-plum-700/70">{trip.day} · {trip.time} · {trip.seats} place{trip.seats > 1 ? "s" : ""}</p>
        </DialogHeader>

        {apps.length === 0 ? (
          <div className="text-center py-7">
            <Users size={28} className="mx-auto text-plum-700/30 mb-2" />
            <h3 className="font-display font-bold text-plum-900">Aucune candidature pour l&apos;instant</h3>
            <p className="text-[13px] text-plum-700/65 mt-1">Vous serez notifié dès qu&apos;un habitant candidate.</p>
          </div>
        ) : (
          <ul className="space-y-2">
            {apps.map((a) => (
              <li key={a.id} className="phenix-card bg-white p-3" data-testid={`application-${a.id}`}>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-mint-100 text-mint-600 grid place-items-center font-display font-extrabold text-sm shrink-0">
                    {(a.user_pseudo || "?").charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline justify-between gap-2 mb-0.5">
                      <strong className="text-plum-900 text-sm">{a.user_pseudo}</strong>
                      <span className={`text-[10px] font-bold uppercase tracking-wider ${
                        a.status === "accepted" ? "text-mint-600" : a.status === "rejected" ? "text-pink-500" : "text-sun-700"
                      }`}>
                        {a.status === "accepted" ? "Acceptée" : a.status === "rejected" ? "Refusée" : "En attente"}
                      </span>
                    </div>
                    {a.user_phone && <div className="text-[11px] text-plum-700/70 mb-1">📞 {a.user_phone}</div>}
                    {a.message && <p className="text-[12px] text-plum-700/80 italic leading-snug">« {a.message} »</p>}
                    {a.status === "pending" && (
                      <div className="flex gap-2 mt-2.5">
                        <button
                          onClick={() => moderate(a.id, "accepted")}
                          disabled={busy === a.id}
                          className="flex-1 rounded-full bg-mint-600 hover:bg-mint-700 text-white px-3 py-2 text-[12px] font-bold inline-flex items-center justify-center gap-1.5 disabled:opacity-50"
                          data-testid={`accept-${a.id}`}
                        >
                          <UserCheck size={12} /> Accepter
                        </button>
                        <button
                          onClick={() => moderate(a.id, "rejected")}
                          disabled={busy === a.id}
                          className="flex-1 rounded-full bg-white border border-cream-200 hover:bg-pink-50 text-plum-700/75 hover:text-pink-500 px-3 py-2 text-[12px] font-bold inline-flex items-center justify-center gap-1.5 disabled:opacity-50"
                          data-testid={`reject-${a.id}`}
                        >
                          <UserX size={12} /> Refuser
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, type = "text", v, on, placeholder, testid }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[11px] uppercase tracking-wider text-plum-700/70 font-semibold">{label}</Label>
      <Input
        type={type}
        value={v}
        onChange={(e) => on(e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
        className="rounded-xl h-11 bg-white border-cream-200"
      />
    </div>
  );
}
